import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Typography } from "../../Components/Typography";
import IdeaFlowCard from "../../Components/IdeaFlowCard";
import EngagementsFilterBar, {
  filterEngagementIdeas,
} from "../../Components/EngagementsFilterBar";
import { useAuth } from "../../Context/authContext";
import { getIdeasByUserId } from "../../Utils/apiServices";
import {
  extractIdeasData,
  mapIdeaRecordToCardData,
  getActiveStageIndexFromCurrentStep,
} from "../IdeasReview/utils";

const StudentEngagements = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");

  const fetchStudentEngagements = async (userId, setLoadingState = true) => {
    if (!userId) {
      if (setLoadingState) setLoading(false);
      return;
    }

    try {
      if (setLoadingState) setLoading(true);
      const response = await getIdeasByUserId(userId);
      const ideasData = extractIdeasData(response);
      setEngagements(ideasData.map(mapIdeaRecordToCardData).filter(Boolean));
    } catch (error) {
      console.error("Error fetching student engagements:", error);
      setEngagements([]);
    } finally {
      if (setLoadingState) setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "student" && user?.id) {
      fetchStudentEngagements(user.id);
    }
  }, [user?.id, user?.role]);

  const filteredEngagements = useMemo(
    () => filterEngagementIdeas(engagements, { searchTerm, statusFilter, stageFilter }),
    [engagements, searchTerm, statusFilter, stageFilter]
  );

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setStageFilter("");
  };

  if (loading) {
    return (
      <div className="flex flex-col w-full h-auto gap-8 bg-primary px-8 py-8">
        <div className="flex items-center justify-center py-20">
          <Typography as="p" className="text-md text-tertiary-600">
            Loading engagements...
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-auto gap-8 bg-primary px-8 py-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between w-full sm:gap-4">
        <div className="flex flex-col gap-2 items-start justify-start">
          <Typography as="h1" className="text-2xl font-bold text-primary-900">
            My Engagements
          </Typography>
          <Typography as="p" className="text-md text-tertiary-600">
            View all ideas you&apos;re working on and track your progress.
          </Typography>
        </div>
        <div className="relative shrink-0">
          <EngagementsFilterBar
            ideas={engagements}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            stageFilter={stageFilter}
            onStageChange={setStageFilter}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters((open) => !open)}
            onClearFilters={clearFilters}
          />
        </div>
      </div>

      {engagements.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Typography as="p" className="text-md text-tertiary-600">
            No engagements found.
          </Typography>
        </div>
      ) : filteredEngagements.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 bg-white border border-slate-200 rounded-xl">
          <Icon icon="lucide:filter-x" className="h-10 w-10 text-slate-300" />
          <Typography as="p" className="text-md text-slate-600">
            No engagements match your filters
          </Typography>
          <button
            type="button"
            onClick={clearFilters}
            className="text-sm font-medium text-violet-600 hover:underline"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredEngagements.map((item) => (
            <IdeaFlowCard
              key={item.id}
              idea={item}
              onClick={() =>
                navigate(`/engagements/${item.id}`, {
                  state: {
                    initialStage: getActiveStageIndexFromCurrentStep(item.currentStep ?? 1),
                    mentor: item.mentor,
                    primaryStudent: item.primaryStudent,
                  },
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentEngagements;
